import {createStore, useStore} from "zustand"
import {immer} from "zustand/middleware/immer"
import {useEffect, useRef} from "react"
import {devtools} from "zustand/middleware"

export type Store = ReturnType<typeof createMinMaxStore>

export type State = {
  dataStatus: "default" | "set"
  initialData: {min: number; max: number | undefined}
  error?: string
} & (
  | {
      isEditing: true
      updates?: {min: number | undefined; max: number | undefined}
    }
  | {
      isEditing?: false
    }
)
type EditingState = Extract<State, {isEditing: true}>

export type Actions = {
  beginUpdate: () => void
  cancelUpdate: () => void
  setMin: (min: number | undefined) => void
  setMax: (max: number | undefined) => void
  setError: (error: string | undefined) => void
}

const createMinMaxStore = (initialState: State) =>
  createStore<State & {actions: Actions}>()(
    devtools(
      immer((set) => ({
        ...initialState,
        actions: {
          beginUpdate: () => {
            set(
              (state) => {
                state.error = undefined
                state.isEditing = true
                assertEditing(state)
                if (state.dataStatus === "set") {
                  state.updates = state.initialData
                }
              },
              undefined,
              "beginUpdate"
            )
          },

          cancelUpdate: () => {
            set(
              (state) => {
                state.error = undefined
                if (state.isEditing) {
                  state.updates = undefined
                }
                state.isEditing = false
              },
              undefined,
              "cancelUpdate"
            )
          },

          setMin: (min: number | undefined) => {
            set(
              (state) => {
                assertEditing(state)
                state.error = undefined
                ;(state.updates ??= state.initialData).min = min
              },
              undefined,
              "setMin"
            )
          },

          setMax: (max: number | undefined) => {
            set(
              (state) => {
                assertEditing(state)
                state.error = undefined
                ;(state.updates ??= state.initialData).max = max
              },
              undefined,
              "setMax"
            )
          },

          setError: (error: string | undefined) => {
            set(
              (state) => {
                state.error = error
              },
              undefined,
              "setError"
            )
          }
        }
      })),
      {name: "MinMaxStore"}
    )
  )

export const useMinMaxReducer = (initialState: State): Store => {
  const store = useRef(createMinMaxStore(initialState)).current
  useEffect(() => {
    // keep store in sync with external updates
    const shouldReplace = true
    store.setState({...initialState, actions: {...store.getState().actions}}, shouldReplace)
  }, [initialState, store])

  return store
}

export const useMinMaxReducerActions = (store: Store) => {
  return useStore(store, (s) => s.actions)
}

export const useMinMaxReducerState = (store: Store): State => {
  return useStore(store, (s) => s)
}

function assertEditing(state: State): asserts state is EditingState {
  if (!state.isEditing) {
    throw new Error("State is not in editing mode")
  }
}
